import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import { Checkbox } from '@sorare/core/src/atoms/inputs/Checkbox';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import type { Option } from '@sorare/core/src/components/search/Option';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { isType } from '@sorare/core/src/lib/gql';

import { isMarketPromotion } from '#src/lib/promotionalEvent.ts';

const FILTER = VIRTUAL_TOGGLE_FILTERS.promotionalEventFilter;

const Wrapper = styled(Vertical)`
  gap: 1px;
  border-radius: inherit;
  position: relative;
  &::after {
    content: '';
    inset: 0;
    position: absolute;
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px var(--c-nd-200);
    pointer-events: none;
  }
`;

const PromoWrapper = styled.label`
  display: flex;
  justify-content: space-between;
  background: var(--background);
  padding: var(--unit) var(--double-unit);
  cursor: pointer;
  isolation: isolate;
  color: var(--c-nd-500);
  text-shadow: 1px 1px var(--half-unit) var(--c-nl-900);
  position: relative;
  &:before {
    content: '';
    inset: 0;
    position: absolute;
    border-radius: inherit;
    z-index: 0;
    background: var(--c-nl-300);
    pointer-events: none;
    opacity: 1;
    transition: opacity ease-in 0.1s;
  }
  &.checked,
  &:hover {
    &:before {
      opacity: 0;
    }
  }
  > * {
    z-index: 1;
  }
`;

const FilterOption = ({
  promotionalEvent,
  ...rest
}: {
  promotionalEvent: ReturnType<
    typeof useConfigContext
  >['promotionalEvents'][number];
} & Omit<ComponentProps<typeof Option>, 'label' | 'variant' | 'children'>) => {
  const checked = !!rest.active;

  return (
    <PromoWrapper
      style={
        {
          '--background': `url(${promotionalEvent.filterPictureUrl || promotionalEvent.pictureUrl})`,
        } as React.CSSProperties
      }
      className={classNames({ checked })}
    >
      <LabelL bold color="var(--c-white)">
        {promotionalEvent.name}
      </LabelL>
      <Checkbox checked={checked} noPadding onChange={rest.onClick} />
    </PromoWrapper>
  );
};

const Filter = ({
  visible,
  hideIfNoInstantBuy = false,
  hideCartPurchaseReward = false,
}: {
  visible: boolean;
  hideIfNoInstantBuy: boolean;
  hideCartPurchaseReward: boolean;
}) => {
  const track = useEvents();
  const { promotionalEvents } = useConfigContext();
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });

  const handleChange = (promo: string) => {
    const isActive = currentRefinement === promo;
    const newValue = isActive ? undefined : promo;
    setVirtualToggle({
      [FILTER.name]: newValue,
    });

    if (!isActive) {
      track('Use Market Filter', {
        filterName: FILTER.trackingName,
        filterValue: promo,
      });
    }
  };

  const { value: promotionalEventsWithSearchFilter = [] } = useAsync(
    async () =>
      (
        await Promise.all(
          promotionalEvents.map(async pe => {
            if (!isMarketPromotion(pe)) {
              return null;
            }

            const shouldShow = (() => {
              if (isType(pe, 'CartPurchaseRewardPromotionalEvent'))
                return !hideCartPurchaseReward;
              if (!hideIfNoInstantBuy) {
                return true;
              }

              if ('hasInstantBuy' in pe && pe.hasInstantBuy) {
                return pe.hasInstantBuy;
              }
              return false;
            })();

            return shouldShow ? pe : null;
          })
        )
      ).filter(Boolean),
    [hideIfNoInstantBuy, promotionalEvents]
  );

  if (!visible)
    return <FilterSection hidden={!promotionalEventsWithSearchFilter.length} />;

  return (
    <Wrapper
      className={classNames('FilterWidget', {
        visible: promotionalEventsWithSearchFilter.length,
      })}
    >
      {promotionalEventsWithSearchFilter.map(promotionalEvent => (
        <FilterOption
          promotionalEvent={promotionalEvent}
          key={promotionalEvent.slug}
          active={currentRefinement === promotionalEvent.slug}
          onClick={() => handleChange(promotionalEvent.slug)}
        />
      ))}
    </Wrapper>
  );
};

export const widget: FilterWidget = {
  key: FILTER.name,
  type: 'raw',
  filter: FILTER,
  Component: ({ visible }) => (
    <Filter
      visible={visible}
      hideIfNoInstantBuy={false}
      hideCartPurchaseReward
    />
  ),
};

export const PromotionalEventFilterForInstantBuy: FilterWidget = {
  key: FILTER.name,
  type: 'raw',
  filter: FILTER,
  Component: ({ visible }) => (
    <Filter
      visible={visible}
      hideIfNoInstantBuy
      hideCartPurchaseReward={false}
    />
  ),
};
